import { useState, useMemo, useCallback, useEffect, useRef } from 'react';
import { format, parseISO, differenceInDays, isPast, isFuture, isToday } from 'date-fns';
import { useScreenData } from '@/hooks/useScreenData';
import {
  Search,
  LayoutGrid,
  LayoutList,
  X,
  Eye,
  Calendar,
  User,
  Building2,
  FolderKanban,
  SlidersHorizontal,
  ChevronDown,
  ChevronUp,
  Zap,
  Map,
  PauseCircle,
  CheckCircle2,
  ArrowUpDown,
  Loader2,
} from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { Progress } from '@/components/ui/progress';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  DemoProject,
  ProjectStatus,
  ProjectType,
  ProjectPriority,
  PROJECT_STATUS_CONFIG,
  PROJECT_TYPE_CONFIG,
  PROJECT_PRIORITY_CONFIG,
} from '@/data/mockProjectsData';
import { projectsApi, Project as ApiProject } from '@/services/api';
import { ProjectDetailsModal } from '@/components/projects/ProjectDetailsModal';
import { useCompany } from '@/contexts/CompanyContext';
import { useScheduler } from '@/contexts/SchedulerContext';
import { Project as MockProject, CostCentre } from '@/types';

type ViewMode = 'card' | 'list';

// ─── Helpers ────────────────────────────────────────────────────────────────

function formatDate(iso: string) {
  return format(parseISO(iso), 'dd MMM yyyy');
}

function daysRemaining(endDate: string): number {
  return differenceInDays(parseISO(endDate), new Date());
}

/**
 * Normalize EBProjects status to the ProjectStatus union used by the UI.
 * Uses the Status string first, then falls back to StatusId if the string is null/empty.
 * EBProjects StatusId mapping: 1=Planning, 2=In Progress, 3=On Hold, 4=Cancelled, 5=Completed.
 */
function normalizeStatus(raw: string | null | undefined, statusId?: number | null): ProjectStatus {
  const s = (raw ?? '').trim().toLowerCase();
  if (s === 'planning' || s === 'planned') return 'planning';
  if (s === 'in progress' || s === 'in-progress' || s === 'inprogress' || s === 'active' || s === 'reopened') return 'in progress';
  if (s === 'on hold' || s === 'on-hold' || s === 'onhold' || s === 'suspended') return 'on hold';
  if (s === 'cancelled' || s === 'canceled' || s === 'archived') return 'cancelled';
  if (s === 'completed' || s === 'complete' || s === 'finished' || s === 'closed') return 'completed';
  if (s === 'estimate' || s === 'opportunity') return 'planning';

  // Fall back to StatusId when Status string is null/empty/unrecognized
  if (statusId != null) {
    switch (statusId) {
      case 1: return 'planning';
      case 2: return 'in progress';
      case 3: return 'on hold';
      case 4: return 'cancelled';
      case 5: return 'completed';
    }
  }

  return 'planning';
}

/**
 * Normalize EBProjects type to the ProjectType union.
 * Maps common type strings; defaults to 'time-materials'.
 */
function normalizeType(raw: string | null | undefined): ProjectType {
  const s = (raw ?? '').trim().toLowerCase();
  if (s === 'fixed price' || s === 'fixed-price' || s === 'fixedprice') return 'fixed-price';
  if (s === 'retainer' || s === 'recurring') return 'retainer';
  return 'time-materials';
}

/**
 * Normalize EBProjects priority to the ProjectPriority union.
 * Maps common priority strings; defaults to 'medium'.
 */
function normalizePriority(raw: string | null | undefined): ProjectPriority {
  const s = (raw ?? '').trim().toLowerCase();
  if (s === 'critical' || s === 'urgent') return 'critical';
  if (s === 'high') return 'high';
  if (s === 'low') return 'low';
  return 'medium';
}

/**
 * Maps a unified Project (from the backend /projects endpoint) to the DemoProject
 * interface used by the UI. The backend routes to the correct data provider
 * (EBProjects REST, Financial Operations GraphQL, or Access Financials GraphQL)
 * and returns a normalised Project model.
 */
function mapMockProjectToDemoProject(p: MockProject): DemoProject {
  const budgetValue = p.budgetDays ?? 0;
  const consumed = p.consumedDays ?? 0;
  const percentComplete = budgetValue > 0 ? Math.min(100, Math.round((consumed / budgetValue) * 100)) : 0;
  return {
    id: String(p.id),
    code: p.code ?? '',
    name: p.name,
    description: p.description ?? '',
    status: normalizeStatus(p.status),
    type: 'time-materials',
    priority: 'medium',
    template: '',
    customerName: undefined,
    customerCode: undefined,
    projectManagerId: '',
    projectManagerName: '',
    startDate: new Date().toISOString(),
    endDate: new Date(Date.now() + 90 * 86400000).toISOString(),
    percentComplete,
    budgetedHours: 0,
    actualHours: 0,
    budgetedValue: budgetValue,
    actualCost: consumed,
    projectUrl: '',
    team: [],
    milestones: [],
    budgetItems: [],
    timeEntries: [],
    tags: [],
    region: '',
  };
}

function mapApiProjectToDemoProject(p: ApiProject): DemoProject {
  // Use actual StartDate/EndDate from the synced project; fall back to createdAt/updatedAt
  const startDate = p.startDate ?? p.createdAt ?? new Date().toISOString();
  const endDate = p.endDate ?? p.updatedAt ?? new Date().toISOString();

  // Budget: budgetDays holds TotalCostsBudget, consumedDays holds TotalActualCosts
  const budgetValue = p.budgetDays ?? 0;
  const actualCost = p.consumedDays ?? 0;
  const percentComplete = budgetValue > 0 ? Math.min(100, Math.round((actualCost / budgetValue) * 100)) : 0;

  return {
    id: p.id ?? '',
    code: p.billingCode ?? '',
    name: p.name ?? '',
    description: p.projectDescription ?? '',
    status: normalizeStatus(p.status),
    type: 'time-materials',
    priority: 'medium',
    template: '',
    customerName: p.clientName || undefined,
    customerCode: undefined,
    projectManagerId: '',
    projectManagerName: p.projectManager ?? '',
    startDate,
    endDate,
    percentComplete,
    budgetedHours: 0,
    actualHours: 0,
    budgetedValue: budgetValue,
    actualCost,
    projectUrl: '',
    team: [],
    milestones: [],
    budgetItems: [],
    timeEntries: [],
    tags: [],
    region: '',
  };
}

// ─── Status filter pills ─────────────────────────────────────────────────────

const ALL_STATUSES: Array<{ value: ProjectStatus | 'all'; label: string }> = [
  { value: 'all',         label: 'All' },
  { value: 'planning',    label: 'Planning' },
  { value: 'in progress', label: 'In Progress' },
  { value: 'on hold',     label: 'On Hold' },
  { value: 'completed',   label: 'Completed' },
  { value: 'cancelled',   label: 'Cancelled' },
];

// ─── Project Card ────────────────────────────────────────────────────────────

function ProjectCard({ project, onClick }: { project: DemoProject; onClick: () => void }) {
  const statusCfg = PROJECT_STATUS_CONFIG[project.status];
  const days      = daysRemaining(project.endDate);
  const isOverdue = days < 0 && project.status !== 'completed' && project.status !== 'cancelled';

  return (
    <Card
      onClick={onClick}
      className="group cursor-pointer transition-all duration-150 hover:shadow-md hover:border-border/80 hover:-translate-y-px flex flex-col"
    >
      <CardContent className="flex-1 flex flex-col gap-3 p-4">

        {/* Row 1 – Status dot + label (only semantic info kept) */}
        <div className="flex items-center justify-between gap-2">
          <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <span className={`h-2 w-2 rounded-full shrink-0 ${statusCfg.dotClass}`} />
            {statusCfg.label}
          </span>
          {isOverdue && (
            <span className="text-[10px] font-medium text-destructive bg-destructive/8 px-1.5 py-0.5 rounded-full">
              {Math.abs(days)}d overdue
            </span>
          )}
        </div>

        {/* Row 2 – Code + Name */}
        <div>
          <p className="text-xs text-muted-foreground leading-none mb-1">{project.code}</p>
          <h3 className="font-semibold text-sm leading-snug group-hover:text-primary transition-colors line-clamp-2">
            {project.name}
          </h3>
        </div>

        {/* Row 3 – Customer */}
        {project.customerName ? (
          <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            <Building2 className="h-3.5 w-3.5 shrink-0 opacity-60" />
            <span className="truncate">{project.customerName}</span>
          </div>
        ) : null}

        {/* Row 4 – Project Manager */}
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <User className="h-3.5 w-3.5 shrink-0 opacity-60" />
          <span className="truncate">{project.projectManagerName}</span>
        </div>

        {/* Row 5 – End date */}
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
          <Calendar className="h-3.5 w-3.5 shrink-0 opacity-60" />
          <span>Due {formatDate(project.endDate)}</span>
          {!isOverdue && project.status !== 'completed' && project.status !== 'cancelled' && days <= 14 && (
            <span className="text-amber-600 font-medium">&middot; {days}d left</span>
          )}
        </div>

        {/* Divider */}
        <div className="border-t border-dashed" />

        {/* Progress */}
        <div className="space-y-1.5">
          <div className="flex items-center justify-between text-[10px] text-muted-foreground">
            <span>Progress</span>
            <span className="font-semibold text-foreground tabular-nums">{project.percentComplete}%</span>
          </div>
          <Progress
            value={project.percentComplete}
            className="h-1"
          />
        </div>

      </CardContent>
    </Card>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export function Projects() {
  const { bookings: allBookings, resources, currentMode } = useScreenData('calendar');
  const { mockData } = useScheduler();
  const isDemo = currentMode === 'demo';
  const [searchQuery,    setSearchQuery]    = useState('');
  const [statusFilter,   setStatusFilter]   = useState<ProjectStatus | 'all'>('all');
  const [typeFilter,     setTypeFilter]     = useState<ProjectType | 'all'>('all');
  const [priorityFilter, setPriorityFilter] = useState<ProjectPriority | 'all'>('all');
  const [regionFilter,   setRegionFilter]   = useState<string>('all');
  const [pmFilter,       setPmFilter]       = useState<string>('all');
  const [viewMode,       setViewMode]       = useState<ViewMode>('list');
  const [selectedProject, setSelectedProject] = useState<DemoProject | null>(null);
  const [isDetailsOpen,  setIsDetailsOpen]  = useState(false);
  const [showFilters,    setShowFilters]    = useState(false);
  const [sortBy,         setSortBy]         = useState<'name' | 'code' | 'status' | 'endDate' | 'progress'>('name');
  const [sortDir,        setSortDir]        = useState<'asc' | 'desc'>('asc');

  // ── Company context ──────────────────────────────────────────────────────
  const { currentCompany } = useCompany();

  // ── Live project data from backend (routed to correct provider per company) ─
  const [liveProjects, setLiveProjects] = useState<DemoProject[]>([]);
  const [isLoadingProjects, setIsLoadingProjects] = useState(true);
  const [projectsError, setProjectsError] = useState<string | null>(null);

  // Monotonic counter bumped by the 'company-changed' custom event so the
  // fetch effect is guaranteed to re-run even when React's dependency
  // comparison doesn't detect a change (e.g. rapid switches, same boolean).
  const [companyRefreshKey, setCompanyRefreshKey] = useState(0);

  useEffect(() => {
    function onCompanyChanged() {
      setCompanyRefreshKey((k) => k + 1);
    }
    window.addEventListener('company-changed', onCompanyChanged);
    window.addEventListener('company-access-changed', onCompanyChanged);
    return () => {
      window.removeEventListener('company-changed', onCompanyChanged);
      window.removeEventListener('company-access-changed', onCompanyChanged);
    };
  }, []);

  // Build a resource name lookup from the resources already loaded in SchedulerContext
  const resourceNameLookup = useMemo(() => {
    const lookup: Record<string, string> = {};
    resources.forEach((r) => {
      if (r.id && r.name) lookup[r.id] = r.name;
    });
    return lookup;
  }, [resources]);

  // Keep ref so the fetch closure always reads the latest value without
  // adding it as an effect dependency (which could cause extra fetches).
  const resourceNameLookupRef = useRef(resourceNameLookup);
  resourceNameLookupRef.current = resourceNameLookup;

  useEffect(() => {
    // No company selected (user has no company access) — show empty list
    if (!currentCompany) {
      setLiveProjects([]);
      setIsLoadingProjects(false);
      return;
    }

    // In demo mode, use mock data (already company-filtered by SchedulerContext)
    if (isDemo) {
      const mapped = mockData.projects.map(mapMockProjectToDemoProject);
      setLiveProjects(mapped);
      setIsLoadingProjects(false);
      return;
    }

    let cancelled = false;

    async function fetchAllProjects() {
      setIsLoadingProjects(true);
      setProjectsError(null);

      try {
        // Call the /projects endpoint with the active company ID so the backend
        // returns only that company's synced projects (or all projects when no
        // integration is configured for the company).
        const apiProjects: ApiProject[] = await projectsApi.getAll(currentCompany?.id);

        if (cancelled) return;

        // Map unified Project model to DemoProject for the UI
        const mapped = apiProjects.map((p) => mapApiProjectToDemoProject(p));

        setLiveProjects(mapped);
      } catch (err) {
        if (cancelled) return;
        console.warn('Failed to fetch projects from API:', err);
        setLiveProjects([]);
      } finally {
        if (!cancelled) setIsLoadingProjects(false);
      }
    }

    fetchAllProjects();
    return () => { cancelled = true; };
  // currentCompany.id + companyRefreshKey: two independent triggers so the
  // fetch fires on every company switch regardless of React batching timing.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentCompany?.id, companyRefreshKey, isDemo, mockData.projects]);

  // ── Derived filter options ─────────────────────────────────────────────────
  const regions = useMemo(
    () => Array.from(new Set(liveProjects.map((p) => p.region).filter(Boolean))).sort(),
    [liveProjects],
  );

  const projectManagers = useMemo((): { id: string; name: string }[] => {
    const seen   = new Set<string>();
    const result: { id: string; name: string }[] = [];
    liveProjects.forEach((p) => {
      if (p.projectManagerId && !seen.has(p.projectManagerId)) {
        seen.add(p.projectManagerId);
        result.push({ id: p.projectManagerId, name: p.projectManagerName });
      }
    });
    return result.sort((a, b) => a.name.localeCompare(b.name));
  }, [liveProjects]);

  // ── Filtered + sorted list ─────────────────────────────────────────────────
  const filtered = useMemo(() => {
    const q = searchQuery.toLowerCase();
    return liveProjects
      .filter((p) => {
        const matchSearch =
          !q ||
          p.name.toLowerCase().includes(q) ||
          p.code.toLowerCase().includes(q) ||
          (p.customerName?.toLowerCase().includes(q) ?? false) ||
          p.projectManagerName.toLowerCase().includes(q) ||
          p.tags.some((t) => t.toLowerCase().includes(q));
        const matchStatus   = statusFilter   === 'all' || p.status            === statusFilter;
        const matchType     = typeFilter     === 'all' || p.type              === typeFilter;
        const matchPriority = priorityFilter === 'all' || p.priority          === priorityFilter;
        const matchRegion   = regionFilter   === 'all' || p.region            === regionFilter;
        const matchPm       = pmFilter       === 'all' || p.projectManagerId  === pmFilter;
        return matchSearch && matchStatus && matchType && matchPriority && matchRegion && matchPm;
      })
      .sort((a, b) => {
        let cmp = 0;
        switch (sortBy) {
          case 'code':     cmp = a.code.localeCompare(b.code);              break;
          case 'status':   cmp = a.status.localeCompare(b.status);          break;
          case 'endDate':  cmp = a.endDate.localeCompare(b.endDate);        break;
          case 'progress': cmp = a.percentComplete - b.percentComplete;     break;
          default:         cmp = a.name.localeCompare(b.name);
        }
        return sortDir === 'asc' ? cmp : -cmp;
      });
  }, [liveProjects, searchQuery, statusFilter, typeFilter, priorityFilter, regionFilter, pmFilter, sortBy, sortDir]);

  // ── Active filter chips ────────────────────────────────────────────────────
  const activeFilters = useMemo(() => {
    const chips: { key: string; label: string; clear: () => void }[] = [];
    if (searchQuery)           chips.push({ key: 'q',      label: `"${searchQuery}"`,                                       clear: () => setSearchQuery('') });
    if (statusFilter   !== 'all') chips.push({ key: 'status', label: PROJECT_STATUS_CONFIG[statusFilter].label,             clear: () => setStatusFilter('all') });
    if (typeFilter     !== 'all') chips.push({ key: 'type',   label: PROJECT_TYPE_CONFIG[typeFilter].label,                  clear: () => setTypeFilter('all') });
    if (priorityFilter !== 'all') chips.push({ key: 'pri',    label: `${PROJECT_PRIORITY_CONFIG[priorityFilter].label} Priority`, clear: () => setPriorityFilter('all') });
    if (regionFilter   !== 'all') chips.push({ key: 'region', label: regionFilter,                                          clear: () => setRegionFilter('all') });
    if (pmFilter !== 'all') {
      const pm = projectManagers.find((p) => p.id === pmFilter);
      chips.push({ key: 'pm', label: pm?.name ?? pmFilter, clear: () => setPmFilter('all') });
    }
    return chips;
  }, [searchQuery, statusFilter, typeFilter, priorityFilter, regionFilter, pmFilter, projectManagers]);

  const clearAllFilters = useCallback(() => {
    setSearchQuery('');
    setStatusFilter('all');
    setTypeFilter('all');
    setPriorityFilter('all');
    setRegionFilter('all');
    setPmFilter('all');
  }, []);

  const handleViewDetails = useCallback((project: DemoProject) => {
    setSelectedProject(project);
    setIsDetailsOpen(true);
  }, []);

  const toggleSort = (col: typeof sortBy) => {
    if (sortBy === col) setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    else { setSortBy(col); setSortDir('asc'); }
  };

  const SortIcon = ({ col }: { col: typeof sortBy }) =>
    sortBy === col ? (
      sortDir === 'asc'
        ? <ChevronUp   className="h-3 w-3 ml-0.5 inline" />
        : <ChevronDown className="h-3 w-3 ml-0.5 inline" />
    ) : (
      <ArrowUpDown className="h-3 w-3 ml-0.5 inline opacity-30" />
    );

  // ── Summary stats ──────────────────────────────────────────────────────────
  const stats = useMemo(() => ({
    active:    liveProjects.filter((p) => p.status === 'in progress').length,
    planning:  liveProjects.filter((p) => p.status === 'planning').length,
    onHold:    liveProjects.filter((p) => p.status === 'on hold').length,
    completed: liveProjects.filter((p) => p.status === 'completed').length,
    total:     liveProjects.length,
  }), [liveProjects]);

  // ── KPI tile helper ────────────────────────────────────────────────────────
  type KpiDef = {
    status: ProjectStatus;
    count: number;
    label: string;
    sub: string;
    icon: React.ComponentType<{ className?: string }>;
  };

  const kpiTiles: KpiDef[] = [
    { status: 'in progress', count: stats.active,    label: 'Active',    sub: 'In Progress', icon: Zap },
    { status: 'planning',    count: stats.planning,  label: 'Planning',  sub: 'Upcoming',    icon: Map },
    { status: 'on hold',     count: stats.onHold,    label: 'On Hold',   sub: 'Paused',      icon: PauseCircle },
    { status: 'completed',   count: stats.completed, label: 'Completed', sub: 'Delivered',   icon: CheckCircle2 },
  ];

  return (
    <div className="h-full overflow-y-auto overflow-x-hidden">
      <div className="w-full px-6 py-5 space-y-5">

        {/* ── Page Header ─────────────────────────────────────────────── */}
        <div className="flex items-start justify-between gap-4">
          <div>
            <h1 className="text-2xl font-semibold tracking-tight">Projects</h1>
            <p className="text-sm text-muted-foreground mt-0.5">
              Manage and track all projects across your organisation
            </p>
          </div>
          <div className="flex items-center gap-3 shrink-0 pt-1">
            <p className="text-sm text-muted-foreground">
              <span className="font-semibold text-foreground">{filtered.length}</span>
              {' / '}
              <span className="font-semibold text-foreground">{liveProjects.length}</span>
            </p>
          </div>
        </div>

        {/* ── KPI Strip ────────────────────────────────────────────────── */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          {kpiTiles.map(({ status, count, label, sub, icon: Icon }) => {
            const isActive = statusFilter === status;
            const pct = Math.round((count / (stats.total || 1)) * 100);
            return (
              <button
                key={status}
                onClick={() => setStatusFilter(isActive ? 'all' : status)}
                className={[
                  'group relative rounded-xl border p-4 text-left transition-all duration-200 overflow-hidden cursor-pointer',
                  'hover:shadow-md hover:-translate-y-0.5 hover:scale-[1.02] active:scale-[0.99] active:shadow-sm',
                  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                  isActive
                    ? 'bg-primary/5 border-primary/30 shadow-sm'
                    : 'bg-card border-border hover:border-primary/30',
                ].join(' ')}
              >
                <div className="flex items-start justify-between mb-3">
                  <div className={`flex h-8 w-8 items-center justify-center rounded-lg transition-colors ${isActive ? 'bg-primary/10' : 'bg-muted group-hover:bg-muted/80'}`}>
                    <Icon className={`h-4 w-4 transition-colors ${isActive ? 'text-primary' : 'text-muted-foreground'}`} />
                  </div>
                  <span className={`text-[11px] font-medium tabular-nums px-1.5 py-0.5 rounded-full ${isActive ? 'bg-primary/10 text-primary' : 'bg-muted text-muted-foreground'}`}>
                    {pct}%
                  </span>
                </div>
                <p className={`text-2xl font-semibold leading-none mb-1 ${isActive ? 'text-primary' : 'text-foreground'}`}>{count}</p>
                <p className={`text-xs font-medium ${isActive ? 'text-primary' : 'text-foreground'}`}>{label}</p>
                <p className={`text-[11px] mt-0.5 ${isActive ? 'text-primary/70' : 'text-muted-foreground'}`}>{sub}</p>
              </button>
            );
          })}
        </div>

        {/* ── Search + Controls Row ────────────────────────────────────── */}
        <div className="space-y-2">
          <div className="flex items-center gap-2">
            {/* Search */}
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground pointer-events-none" />
              <Input
                placeholder="Search projects…"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 h-9"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>

            {/* Filter toggle */}
            <Button
              variant={showFilters ? 'default' : 'outline'}
              size="sm"
              className="h-9 gap-1.5 shrink-0"
              onClick={() => setShowFilters((v) => !v)}
            >
              <SlidersHorizontal className="h-3.5 w-3.5" />
              Filters
              {activeFilters.filter((f) => f.key !== 'q').length > 0 && (
                <span className="ml-0.5 rounded-full bg-primary-foreground text-primary text-[10px] font-bold h-4 min-w-4 inline-flex items-center justify-center px-1">
                  {activeFilters.filter((f) => f.key !== 'q').length}
                </span>
              )}
            </Button>

            {/* View toggle */}
            <ToggleGroup
              type="single"
              value={viewMode}
              onValueChange={(v) => v && setViewMode(v as ViewMode)}
              variant="outline"
              className="shrink-0 h-9"
            >
              <ToggleGroupItem value="card" aria-label="Card view" className="h-9 w-9">
                <LayoutGrid className="h-4 w-4" />
              </ToggleGroupItem>
              <ToggleGroupItem value="list" aria-label="List view" className="h-9 w-9">
                <LayoutList className="h-4 w-4" />
              </ToggleGroupItem>
            </ToggleGroup>
          </div>

          {/* ── Expanded Filters Panel ─────────────────────────────────── */}
          {showFilters && (
            <div className="rounded-lg border bg-card p-4 space-y-4 animate-in fade-in-0 slide-in-from-top-1 duration-200">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                {/* Status */}
                <div className="space-y-1.5">
                  <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Status</p>
                  <div className="flex flex-wrap gap-1">
                    {ALL_STATUSES.map(({ value, label }) => (
                      <button
                        key={value}
                        onClick={() => setStatusFilter(value as ProjectStatus | 'all')}
                        className={[
                          'px-2.5 h-7 rounded-full text-xs font-medium border transition-colors',
                          statusFilter === value
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-background text-muted-foreground border-input hover:bg-accent',
                        ].join(' ')}
                      >
                        {value !== 'all' && (
                          <span className={`mr-1 h-1.5 w-1.5 rounded-full ${PROJECT_STATUS_CONFIG[value as ProjectStatus].dotClass} inline-block`} />
                        )}
                        {label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Type */}
                <div className="space-y-1.5">
                  <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Type</p>
                  <div className="flex flex-wrap gap-1">
                    {(['all', 'fixed-price', 'time-materials', 'retainer'] as const).map((val) => (
                      <button
                        key={val}
                        onClick={() => setTypeFilter(val)}
                        className={[
                          'px-2.5 h-7 rounded-full text-xs font-medium border transition-colors',
                          typeFilter === val
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-background text-muted-foreground border-input hover:bg-accent',
                        ].join(' ')}
                      >
                        {val === 'all' ? 'All Types' : PROJECT_TYPE_CONFIG[val].label}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Priority */}
                <div className="space-y-1.5">
                  <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Priority</p>
                  <div className="flex flex-wrap gap-1">
                    {(['all', 'critical', 'high', 'medium', 'low'] as const).map((val) => (
                      <button
                        key={val}
                        onClick={() => setPriorityFilter(val)}
                        className={[
                          'px-2.5 h-7 rounded-full text-xs font-medium border transition-colors',
                          priorityFilter === val
                            ? 'bg-primary text-primary-foreground border-primary'
                            : 'bg-background text-muted-foreground border-input hover:bg-accent',
                        ].join(' ')}
                      >
                        {val === 'all' ? 'All' : PROJECT_PRIORITY_CONFIG[val].label}
                      </button>
                    ))}
                  </div>
                </div>
              </div>

              {/* Dropdowns row */}
              <div className="flex flex-wrap gap-3 items-end pt-3 border-t">
                <div className="space-y-1">
                  <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Region</p>
                  <Select value={regionFilter} onValueChange={setRegionFilter}>
                    <SelectTrigger className="h-8 w-[160px] text-xs">
                      <SelectValue placeholder="All Regions" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Regions</SelectItem>
                      {regions.map((r) => (
                        <SelectItem key={r} value={r}>{r}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Project Manager</p>
                  <Select value={pmFilter} onValueChange={setPmFilter}>
                    <SelectTrigger className="h-8 w-[200px] text-xs">
                      <SelectValue placeholder="All PMs" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Project Managers</SelectItem>
                      {projectManagers.map(({ id, name }) => (
                        <SelectItem key={id} value={id}>{name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-1">
                  <p className="text-[10px] font-medium text-muted-foreground uppercase tracking-wider">Sort By</p>
                  <div className="flex items-center gap-1">
                    <Select value={sortBy} onValueChange={(v) => setSortBy(v as typeof sortBy)}>
                      <SelectTrigger className="h-8 w-[140px] text-xs">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="name">Name</SelectItem>
                        <SelectItem value="code">Code</SelectItem>
                        <SelectItem value="status">Status</SelectItem>
                        <SelectItem value="endDate">End Date</SelectItem>
                        <SelectItem value="progress">Progress</SelectItem>
                      </SelectContent>
                    </Select>
                    <Button
                      variant="outline"
                      size="icon"
                      className="h-8 w-8 shrink-0"
                      onClick={() => setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'))}
                    >
                      {sortDir === 'asc'
                        ? <ChevronUp   className="h-3.5 w-3.5" />
                        : <ChevronDown className="h-3.5 w-3.5" />}
                    </Button>
                  </div>
                </div>

                {activeFilters.length > 0 && (
                  <button
                    onClick={clearAllFilters}
                    className="ml-auto text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
                  >
                    Clear all
                  </button>
                )}
              </div>
            </div>
          )}

          {/* ── Active filter chips ──────────────────────────────────── */}
          {activeFilters.length > 0 && (
            <div className="flex flex-wrap items-center gap-1.5">
              {activeFilters.map((f) => (
                <span
                  key={f.key}
                  className="inline-flex items-center gap-1 pl-2.5 pr-1.5 py-0.5 rounded-full text-xs font-medium bg-secondary text-secondary-foreground border border-border"
                >
                  {f.label}
                  <button
                    onClick={f.clear}
                    className="ml-0.5 rounded-full p-0.5 hover:bg-muted-foreground/20 transition-colors"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </span>
              ))}
              {activeFilters.length > 1 && (
                <button
                  onClick={clearAllFilters}
                  className="text-xs text-muted-foreground hover:text-foreground underline underline-offset-2 transition-colors"
                >
                  Clear all
                </button>
              )}
            </div>
          )}
        </div>

        {/* ── Loading / Error states ────────────────────────────────── */}
        {isLoadingProjects && (
          <div className="py-20 flex flex-col items-center gap-3 text-muted-foreground">
            <Loader2 className="h-8 w-8 animate-spin opacity-40" />
            <p className="text-sm">Loading projects...</p>
          </div>
        )}

        {projectsError && !isLoadingProjects && (
          <div className="py-20 flex flex-col items-center gap-3 text-muted-foreground">
            <FolderKanban className="h-10 w-10 opacity-20" />
            <p className="text-sm text-destructive">{projectsError}</p>
            <button
              onClick={() => window.location.reload()}
              className="text-xs text-primary underline underline-offset-2"
            >
              Retry
            </button>
          </div>
        )}

        {/* ── Card View ────────────────────────────────────────────────── */}
        {!isLoadingProjects && !projectsError && viewMode === 'card' && (
          <div className="grid gap-3 grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4">
            {filtered.length === 0 ? (
              <div className="col-span-full py-20 flex flex-col items-center gap-3 text-muted-foreground">
                <FolderKanban className="h-10 w-10 opacity-20" />
                <p className="text-sm">No projects match your filters.</p>
                <button onClick={clearAllFilters} className="text-xs text-primary underline underline-offset-2">
                  Clear filters
                </button>
              </div>
            ) : (
              filtered.map((project) => (
                <ProjectCard
                  key={project.id}
                  project={project}
                  onClick={() => handleViewDetails(project)}
                />
              ))
            )}
          </div>
        )}

        {/* ── List View ─────────────────────────────────────────────────── */}
        {!isLoadingProjects && !projectsError && viewMode === 'list' && (
          <div className="rounded-lg border overflow-hidden">
            <Table>
              <TableHeader>
                <TableRow className="hover:bg-transparent bg-muted/30">
                  <TableHead className="w-[90px] cursor-pointer select-none text-xs" onClick={() => toggleSort('code')}>
                    Code <SortIcon col="code" />
                  </TableHead>
                  <TableHead className="cursor-pointer select-none text-xs" onClick={() => toggleSort('name')}>
                    Name <SortIcon col="name" />
                  </TableHead>
                  <TableHead className="text-xs">Customer</TableHead>
                  <TableHead className="cursor-pointer select-none text-xs" onClick={() => toggleSort('status')}>
                    Status <SortIcon col="status" />
                  </TableHead>
                  <TableHead className="text-xs">PM</TableHead>
                  <TableHead className="cursor-pointer select-none text-xs" onClick={() => toggleSort('endDate')}>
                    Due <SortIcon col="endDate" />
                  </TableHead>
                  <TableHead className="w-[110px] cursor-pointer select-none text-xs" onClick={() => toggleSort('progress')}>
                    Progress <SortIcon col="progress" />
                  </TableHead>
                  <TableHead className="w-[44px]" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {filtered.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-12 text-muted-foreground">
                      <div className="flex flex-col items-center gap-2">
                        <FolderKanban className="h-8 w-8 opacity-20" />
                        <p className="text-sm">No projects found</p>
                        <button onClick={clearAllFilters} className="text-xs text-primary underline underline-offset-2">Clear filters</button>
                      </div>
                    </TableCell>
                  </TableRow>
                ) : (
                  filtered.map((project) => {
                    const statusCfg = PROJECT_STATUS_CONFIG[project.status];
                    const days      = daysRemaining(project.endDate);
                    const isOverdue = days < 0 && project.status !== 'completed' && project.status !== 'cancelled';
                    return (
                      <TableRow
                        key={project.id}
                        className="cursor-pointer hover:bg-muted/40 transition-colors"
                        onClick={() => handleViewDetails(project)}
                      >
                        <TableCell className="text-xs text-muted-foreground py-3">
                          {project.code}
                        </TableCell>
                        <TableCell className="py-3">
                          <p className="font-medium text-sm leading-tight">{project.name}</p>
                        </TableCell>
                        <TableCell className="py-3 text-sm text-muted-foreground max-w-[140px] truncate">
                          {project.customerName ?? <span className="opacity-30">—</span>}
                        </TableCell>
                        <TableCell className="py-3">
                          <span className="flex items-center gap-1.5 text-xs text-muted-foreground whitespace-nowrap">
                            <span className={`h-2 w-2 rounded-full shrink-0 ${statusCfg.dotClass}`} />
                            {statusCfg.label}
                          </span>
                        </TableCell>
                        <TableCell className="py-3 text-sm text-muted-foreground whitespace-nowrap">
                          {project.projectManagerName}
                        </TableCell>
                        <TableCell className="py-3">
                          <p className="text-sm whitespace-nowrap">{formatDate(project.endDate)}</p>
                          {isOverdue ? (
                            <p className="text-[10px] text-destructive font-medium">{Math.abs(days)}d overdue</p>
                          ) : project.status !== 'completed' && project.status !== 'cancelled' && days <= 14 ? (
                            <p className="text-[10px] text-amber-600">{days}d remaining</p>
                          ) : null}
                        </TableCell>
                        <TableCell className="py-3">
                          <TooltipProvider>
                            <Tooltip>
                              <TooltipTrigger asChild>
                                <div className="space-y-1 min-w-[90px]">
                                  <Progress value={project.percentComplete} className="h-1.5" />
                                  <p className="text-[10px] text-muted-foreground text-right tabular-nums">{project.percentComplete}%</p>
                                </div>
                              </TooltipTrigger>
                              <TooltipContent side="top">
                                <p className="text-xs">{project.percentComplete}% complete</p>
                              </TooltipContent>
                            </Tooltip>
                          </TooltipProvider>
                        </TableCell>
                        <TableCell className="py-3">
                          <Button
                            variant="ghost"
                            size="icon"
                            className="h-7 w-7 text-muted-foreground hover:text-foreground"
                            onClick={(e) => { e.stopPropagation(); handleViewDetails(project); }}
                          >
                            <Eye className="h-3.5 w-3.5" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </div>
        )}

        {/* ── Cost Centres Section ────────────────────────────────────── */}
        {isDemo && mockData.costCentres.length > 0 && (
          <div className="space-y-3 mt-6">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold tracking-tight">Cost Centres</h2>
                <p className="text-sm text-muted-foreground">
                  Cost centres for the selected company
                </p>
              </div>
              <Badge variant="secondary">{mockData.costCentres.length}</Badge>
            </div>
            <div className="rounded-lg border overflow-hidden">
              <Table>
                <TableHeader>
                  <TableRow className="bg-muted/40">
                    <TableHead className="text-xs font-semibold uppercase tracking-wider w-[120px]">Code</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider">Name</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider">Department</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider">Description</TableHead>
                    <TableHead className="text-xs font-semibold uppercase tracking-wider w-[100px]">Status</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {mockData.costCentres.map((cc: CostCentre) => (
                    <TableRow key={cc.id}>
                      <TableCell className="text-xs text-muted-foreground py-3">{cc.code}</TableCell>
                      <TableCell className="py-3 font-medium text-sm">{cc.name}</TableCell>
                      <TableCell className="py-3 text-sm text-muted-foreground">{cc.department ?? '—'}</TableCell>
                      <TableCell className="py-3 text-sm text-muted-foreground max-w-[200px] truncate">{cc.description ?? '—'}</TableCell>
                      <TableCell className="py-3">
                        <Badge variant={cc.status === 'active' ? 'default' : 'secondary'} className="text-[10px]">
                          {cc.status ?? 'active'}
                        </Badge>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </div>
        )}

      </div>

      {/* ── Project Details Sheet ─────────────────────────────────────── */}
      <ProjectDetailsModal
        project={selectedProject}
        open={isDetailsOpen}
        onOpenChange={setIsDetailsOpen}
        allBookings={allBookings}
        resources={resources}
      />
    </div>
  );
}
